import CustomForm from "../../CustomForm";
import m, {Vnode} from "mithril";
import ICustomFormSettings from "../../ICustomFormSettings";
import ChaptersInfo, {TChapter} from "../ChaptersInfo";
import {TextareaItem} from "../../../model/items/textarea";
import {CheckboxItem} from "../../../model/items/checkbox";
import {Form} from "../../../model/form/form";
import {loadJsonFromString} from "../../shared/helpers";

type TChapterConfig = {
    progress?: 'percent'|'count'|'none',
    showName?: boolean,
    showNumber?: boolean,
    show?: boolean,
    prefix?: string,
    postfix?: string,
    style?: string,
};

type TChaptersConfig = TChapterConfig & {
    chapters?: {[name: string]: TChapterConfig}
};

export default class ChaptersDefault extends CustomForm {

    private chaptersConfig: TChaptersConfig = {};
    private readonly chaptersInfo: ChaptersInfo;

    private readonly chaptersCompletedItem: CheckboxItem | undefined;

    private readonly logItem: TextareaItem | undefined;
    private readonly log: string[];

    public constructor(
        protected settings: ICustomFormSettings
    ) {
        super(settings);

        loadJsonFromString(
            this.settings.form.aux1,
            this.settings.form.questionary,
            config => this.chaptersConfig = config ?? {},
            () => this.chaptersConfig = {}
        );

        this.chaptersInfo = new ChaptersInfo(settings.form);
        this.chaptersCompletedItem = <CheckboxItem>this.settings.form.grid.cellItems.find(ci => ci instanceof CheckboxItem);
        this.logItem = <TextareaItem>this.settings.form.grid.cellItems.find(ci => ci instanceof TextareaItem);
        this.log = JSON.parse( this.logItem.getVariable() || '[]');

        const finished = this.chaptersInfo.chapters.every(chapter => chapter.progress.finished);
        this.chaptersCompletedItem?.setVariable( finished );

        this.controls.onNext = () => this.goToForm(finished ? this.chaptersInfo.afterForm : this.chaptersInfo.lastForm);
    }

    checkRequirements(): boolean {

        if (!this.chaptersCompletedItem) {
            console.log('customForm-Chapter: Missing checkbox to validate Chapters completed.');
            return false;
        }

        if (!this.chaptersInfo.chapters.length) {
            console.log('customForm-Chapter: Can not find any chapters. Did you put chapters in formGroup immediately after this form?');
            return false;
        }

        return super.checkRequirements();
    }

    rewriteFormControls(): boolean {
        return true;
    }

    private wasChapterVisited(chapter: TChapter): boolean {
        return this.log.includes(chapter.name);
    }

    private wasChapterLastVisited(chapter: TChapter): boolean {
        const lastChapterName = this.log[this.log.length - 1];

        return Boolean(lastChapterName && lastChapterName === chapter.name);
    }

    private openChapter(chapter: TChapter): void {
        if (chapter.progress.finished) {
            return;
        }

        this.log.push(chapter.name);
        this.logItem?.setVariable(JSON.stringify(this.log));

        this.goToForm( chapter.progress.lastVisitedForm ?? chapter.orderedForms[0] ?? null );
    }

    /**
     * Replace for questionary.goTo(formId)
     */
    private goToForm(form: Form | null): void {

        const questionary = this.settings.form.questionary;

        questionary.save();
        questionary.currentForm?.stopTiming();
        questionary.currentForm = form;

        if (form) {
            questionary.addToHistory(form.id);
            form.onNavigatedTo();
        }

        m.redraw();
    }


    private getChapterName(config: TChapterConfig ,chapter: TChapter, i: number): string {
        let result = '';
        result += config.prefix ?? '';
        result += (config.showNumber ?? true) ? (i+1)+'. ' : '';
        result += (config.showName ?? true) ? chapter.name : '';
        result += config.postfix ?? '';

        return result;
    }

    private getChapterProgress(config: TChapterConfig, chapter: TChapter): string {
        if (config.progress === 'none') {
            return '';
        }
        if (config.progress === 'percent') {
            return Math.round(100*chapter.progress.current / chapter.progress.total)+' %';
        }

        return chapter.progress.current+'/'+chapter.progress.total;
    }

    private getChapterClassName(chapter: TChapter): string {
        let chapterClass = '.chapter';
        chapterClass += chapter.progress.finished ? '.finished' : '.clickable';
        chapterClass += chapter.progress.inProgress ? '.inProgress' : '';
        chapterClass += this.wasChapterVisited(chapter) ? '.visited' : '.notVisited';
        chapterClass += this.wasChapterLastVisited(chapter) ? '.lastVisited' : '';

        return chapterClass;
    }

    public view(): Vnode {
        const questionary = this.settings.form.questionary;

        return this.viewLayout(
            [
                m('.chapters', {}, this.chaptersInfo.chapters.map( (chapter, i) => {

                    const config = this.chaptersConfig.chapters?.[chapter.name] ?? this.chaptersConfig;
                    if (config.show === false) {
                        return null;
                    }

                    return m(this.getChapterClassName(chapter), {
                        onclick: () => this.openChapter(chapter),
                        style: config.style,
                    }, [
                        m('div.label', {}, this.getChapterName(config, chapter, i)),
                        m('div.progress', {}, this.getChapterProgress(config, chapter)),
                    ]);
                })),
                this.chaptersCompletedItem?.getVariable() ? m('.', {}, [
                    m('p', {}, questionary.renderText(questionary.messages.get('chaptersCompleted'))),
                ]) : null,
            ],
        );
    }

}
